/* eslint-disable max-lines */

import {
    CheckCircleIcon,
    DocumentTextIcon,
    HeroArrowUpTrayIcon,
    NinchatRetryIcon,
    PlusIcon, TrashIcon,
} from 'components/Icon'

import {
    FileItem, QuestionnaireAttachmentViewProps,
} from './types'

import {
    addFilesButton,
    buttonFooter,
    container,
    description,
    fileActions,
    fileErrorMessage,
    fileInfo,
    fileInfoProgress,
    fileItem,
    fileList,
    fileName,
    fileProgress,
    fileSize,
    fileThumbnailContainer,
    fileThumbnailIcon,
    fileThumbnailImage,
    // header,
    sectionTitle,
    topFileInfo,
    uploadSection,
    uploadedIcon,
} from './styles'

import Button from 'components/Button'
import {Icon} from 'components/Icon/types'
import {NinchatEvents} from 'api/types'

import Progress from 'components/Progress'
import React from 'react'
import {UiStore} from 'store/types'

import {fileUtil} from 'utils'

import {styled} from 'stitches/config'
import {useTranslation} from 'react-i18next'

// const StyledHeader = styled('h1', header)

const StyledContainer = styled('div', container)
const StyledDescription = styled('p', description)
const StyledUploadSection = styled('div', uploadSection)
const StyledSectionTitle = styled('div', sectionTitle)

const StyledFileList = styled('div', fileList)
const StyledFileItem = styled('div', fileItem)
const StyledFileInfo = styled('div', fileInfo)
const StyledFileInfoProgress = styled('div', fileInfoProgress)
const StyledTopFileInfo = styled('div', topFileInfo)
const StyledFileProgress = styled('div', fileProgress)
const StyledFileName = styled('div', fileName)
const StyledFileSize = styled('div', fileSize)
const StyledFileActions = styled('div', fileActions)
const StyledUploadedIcon = styled('div', uploadedIcon)
const StyledFileErrorMessage = styled('div', fileErrorMessage)

const StyledThumbnailContainer = styled('div', fileThumbnailContainer)
const StyledThumbnailImage = styled('img', fileThumbnailImage)
const StyledThumbnailIcon = styled('div', fileThumbnailIcon)

const StyledAddFilesButton = styled('button', addFilesButton)
const StyledButtonFooter = styled('div', buttonFooter)


const QuestionnaireAttachmentView = ({
    files,
    filesState,
    hideActions = false,
    onAddFiles,
    onRemoveFile,
    onRetryFile,
    onContinue,
    onUploadFiles,
}: QuestionnaireAttachmentViewProps): React.ReactElement => {
    const {t} = useTranslation()
    const hiddenFileInput = React.useRef<HTMLInputElement>(null)

    const [filesFull, setFilesFull] = React.useState<Array<FileItem & {uploaded?: boolean}>>([])
    const [uploadFilesInProgress, setUploadFilesInProgress] = React.useState(false)

    const mapErrors = (error: NinchatEvents.NinchatErrorEvent) => {
        if (error.error_type === 'upload_quota_exceeded') {
            return t('Upload quota exceeded. Retry or remove attachment.')
        }
        return t('File not uploaded. Retry or remove attachment.')
    }

    React.useEffect(() => {
        setFilesFull(files.map((item) => {
            if (item.thumbnail_url) {
                return {...item}
            } else if (item?.file?.type?.startsWith('image/')) {
                const url = URL.createObjectURL(item.file)
                return {
                    ...item,
                    thumbnail_url: url,
                }
            }
            return item
        }).map((file) => ({
            ...file,
            uploaded: !!file.file_id,
        })))
    }, [files])

    React.useEffect(() => {
        const hasUploadingFiles = filesFull?.some((file) => {
            const state = filesState?.find((f) => f.name === file.name)
            return state?.progress !== undefined && !state.error && !file.uploaded
        })

        setUploadFilesInProgress(hasUploadingFiles)
    }, [filesFull, filesState])

    const handleAddFilesClick = (): void => {
        if (hiddenFileInput.current?.value) {
            hiddenFileInput.current.value = ''
        }
        hiddenFileInput.current?.click()
    }

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.currentTarget.files) {
            onAddFiles(event.currentTarget.files)
        }
    }

    const handleRetryFile = (name: string) => () => {
        onRetryFile(name)
    }

    const handleUploadFiles = (): void => {
        const filesToUpload = filesFull.filter((file) => {
            const state = filesState?.find((f) => f.name === file.name)
            return !file.uploaded && !state?.progress
        }).map((file) => file.name)
        onUploadFiles(filesToUpload)
    }

    const hideUploadButton = uploadFilesInProgress || filesFull.length === 0 || filesFull.every((file) => file.uploaded || filesState?.find((f) => f.name === file.name)?.error)
    const disableContinueButton = uploadFilesInProgress || filesFull.some((file) => !file.uploaded)
    const showHelperText = filesFull.some((file) => !file.uploaded && !filesState?.find((f) => f.name === file.name)?.error) && !uploadFilesInProgress

    const uploadingInProgress = (file: FileItem & {uploaded?: boolean}, state: UiStore.UiStoreAttachmentFile | undefined) =>
        // eslint-disable-next-line implicit-arrow-linebreak
        state?.progress !== undefined && !file.uploaded && !state.error

    const hasError = (state: UiStore.UiStoreAttachmentFile | undefined) => !!state?.error
    const hasUploadedFiles = filesFull.some((file) => file.uploaded)

    const renderFile = (file: FileItem & { uploaded?: boolean}, state: UiStore.UiStoreAttachmentFile | undefined): React.ReactElement => (
        <StyledFileItem invalid={!!state?.error} key={file.name} valid={file.uploaded}>
            <StyledThumbnailContainer>
                {file.thumbnail_url
                    ? (
                        <StyledThumbnailImage
                            alt={file.name}
                            src={file.thumbnail_url}
                        />
                    )
                    : (
                        <StyledThumbnailIcon>
                            <DocumentTextIcon />
                        </StyledThumbnailIcon>
                    )}
            </StyledThumbnailContainer>
            <StyledFileInfo>
                <StyledTopFileInfo>
                    <StyledFileName>{file.name}</StyledFileName>
                    {uploadingInProgress(file, state) ? <StyledFileProgress>{t('Uploading')} {state?.progress} %</StyledFileProgress> : null}
                </StyledTopFileInfo>
                <div>
                    {uploadingInProgress(file, state) ? <StyledFileInfoProgress><Progress progress={state?.progress || 0} /></StyledFileInfoProgress> : null}
                    {hasError(state) ? <StyledFileErrorMessage>{state?.error ? mapErrors(state.error) : ''}</StyledFileErrorMessage> : null}
                    {!hasError(state) && !uploadingInProgress(file, state) ? <StyledFileSize>{fileUtil.formatBytes(file.size)}</StyledFileSize> : null}
                </div>
            </StyledFileInfo>
            <StyledFileActions>
                {hasError(state) ?
                    !hideActions &&
                        <Button
                            color='text'
                            iconLeft={NinchatRetryIcon as unknown as Icon}
                            onPress={handleRetryFile(file.name)}
                            title={t('Retry')}
                        />
                    : file.uploaded ? <StyledUploadedIcon><CheckCircleIcon /></StyledUploadedIcon> : null}
                {!hideActions &&
                    <Button
                        color='text'
                        iconLeft={TrashIcon}
                        onPress={(): void => onRemoveFile(file.name, file.file_id)}
                        title={t('Remove file')}
                    />}
            </StyledFileActions>
        </StyledFileItem>
    )

    return (
        <StyledContainer>
            {/* <StyledHeader>{t('Add files')}</StyledHeader> */}
            {/* <StyledDescription>
                {t('Upload photos you want to show the professional.')}
            </StyledDescription> */}
            <StyledUploadSection>
                {!hasUploadedFiles && <StyledSectionTitle>{t('Choose files to upload')}</StyledSectionTitle>}
                <div>
                    {filesFull.length > 0 && (
                        <StyledFileList>
                            {filesFull.map((file) => {
                                const state = filesState?.find((f) => f.name === file.name)
                                return renderFile(file, state)
                            })}
                        </StyledFileList>
                    )}
                    <input
                        multiple
                        onChange={handleFileChange}
                        ref={hiddenFileInput}
                        style={{display: 'none'}}
                        tabIndex={-1}
                        type='file'
                    />
                    {!hideActions &&
                        <StyledAddFilesButton onClick={handleAddFilesClick}>
                            <div>
                                <PlusIcon />
                            </div>
                            {t('Add files')}
                        </StyledAddFilesButton>}
                </div>
                <div>
                    {!hideUploadButton && !hideActions && (
                        <Button
                            css={{
                                marginTop: '20px',
                                width: '100%',
                            }}
                            iconLeft={HeroArrowUpTrayIcon as unknown as Icon}
                            onPress={handleUploadFiles}
                            text={t('Upload selected files')}
                        />)}
                </div>
            </StyledUploadSection>
            {showHelperText ? <StyledDescription>{t('Upload selected files to continue.')}</StyledDescription> : null}
            {!hideActions && (
                <StyledButtonFooter>
                    <Button
                        color='filled'
                        disabled={disableContinueButton}
                        onPress={onContinue}
                        text={t('Continue')}
                    />
                </StyledButtonFooter>
            )}
        </StyledContainer>

    )
}

export default QuestionnaireAttachmentView
